import { apiClient } from '../../apiHelper';

export type AuditOutcome =
  | 'succeeded'
  | 'accepted'
  | 'partially_succeeded'
  | 'failed'
  | 'denied';

export interface AuditEventFilters {
  search?: string;
  actor?: string;
  action?: string;
  outcome?: AuditOutcome;
  scope?: string;
  created_from?: string;
  created_to?: string;
  cursor?: string;
  limit?: number;
}

export interface AuditActor {
  id?: string | null;
  display_name?: string | null;
  email?: string | null;
  deleted?: boolean;
}

export interface AuditScope {
  id?: string | null;
  name?: string | null;
  type?: string | null;
  deleted?: boolean;
}

export interface AuditDetailField {
  label: string;
  value: string;
  sensitive?: boolean;
}

export interface AuditFieldChange {
  entity_type: string;
  entity_id: string;
  field: string;
  change_kind: 'valued' | 'occurrence';
  impact: 'direct' | 'indirect';
  old_value?: string | null;
  new_value?: string | null;
}

export interface AuditEvent {
  id: string;
  user_id?: string | null;
  actor_identifier?: string | null;
  action: string;
  entity_type?: string | null;
  entity_id?: string | null;
  org_unit_id?: string | null;
  outcome: AuditOutcome;
  reason_code?: string | null;
  changed_fields?: string[] | null;
  context?: Record<string, unknown> | null;
  created_at: string;
  actor?: AuditActor | null;
  scope?: AuditScope | null;
  details?: AuditDetailField[] | null;
  field_changes?: AuditFieldChange[] | null;
}

export interface AuditEventPage {
  items: AuditEvent[];
  next_cursor: string | null;
}

interface AuditEventsApiResponse {
  data?: {
    data?: AuditEventPage;
    items?: AuditEvent[];
    next_cursor?: string | null;
  };
}

export async function getAuditEvents(
  filters: AuditEventFilters,
  signal?: AbortSignal
): Promise<AuditEventPage> {
  const params = new URLSearchParams();
  const apiFilters: Record<string, string | number | undefined> = {
    search: filters.search,
    action: filters.action,
    outcome: filters.outcome,
    scope: filters.scope,
    actor: filters.actor,
    created_from: filters.created_from,
    created_to: filters.created_to,
    cursor: filters.cursor,
    limit: filters.limit ?? 50,
  };
  Object.entries(apiFilters).forEach(([key, value]) => {
    if (value) params.set(key, String(value));
  });
  const response = (await apiClient.get(`/audit-events?${params.toString()}`, {
    signal,
  })) as AuditEventsApiResponse;
  const page = response?.data?.data ?? response?.data;

  return {
    items: page?.items ?? [],
    next_cursor: page?.next_cursor ?? null,
  };
}

export async function getAuditEvent(
  id: string,
  signal?: AbortSignal
): Promise<AuditEvent> {
  const response = (await apiClient.get(`/audit-events/${id}`, { signal })) as {
    data?: { data?: AuditEvent } & AuditEvent;
  };
  return (response?.data?.data ?? response?.data) as AuditEvent;
}

export async function exportAuditEvents(filters: AuditEventFilters): Promise<void> {
  if (!filters.created_from || !filters.created_to) {
    throw new Error('Choose a start and end date before exporting.');
  }

  const params = new URLSearchParams();
  const exportFilters: Record<string, string | undefined> = {
    search: filters.search,
    actor: filters.actor,
    action: filters.action,
    outcome: filters.outcome,
    scope: filters.scope,
    created_from: filters.created_from,
    created_to: filters.created_to,
  };
  Object.entries(exportFilters).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  const response = await apiClient.downloadFile(`/audit-events/export?${params.toString()}`);
  const url = URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = `audit-trail-${filters.created_from.slice(0, 10)}-${filters.created_to.slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}
